import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Function to get system theme preference
const getSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light'; // Default to light if can't detect
};

// Function to get initial theme
const getInitialTheme = () => {
  // Check if user has manually set a preference
  const savedTheme = localStorage.getItem('eevn-theme');
  if (savedTheme) {
    return savedTheme;
  }
  // Otherwise use system preference
  return getSystemTheme();
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => getInitialTheme());
  const [isManualOverride, setIsManualOverride] = useState(() => {
    return !!localStorage.getItem('eevn-theme');
  });

  useEffect(() => {
    // Only save to localStorage if user manually changed theme
    if (isManualOverride) {
      localStorage.setItem('eevn-theme', theme);
    } else {
      // Remove from localStorage to allow system preference to take over
      localStorage.removeItem('eevn-theme');
    }
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme, isManualOverride]);

  // Sync with system theme on mount if not manually overridden
  useEffect(() => {
    if (!isManualOverride) {
      const systemTheme = getSystemTheme();
      setTheme(systemTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Listen to system theme changes if not manually overridden
  useEffect(() => {
    if (!isManualOverride && typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e) => {
        // Update theme when system preference changes
        setTheme(e.matches ? 'dark' : 'light');
      };

      // Modern browsers (Chrome, Firefox, Safari, Edge)
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
      // Fallback for older browsers
      else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    }
  }, [isManualOverride, theme]);

  const toggleTheme = () => {
    setIsManualOverride(true);
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

